import { useRef, useState, type FormEvent } from 'react'
import { Mark } from '@skin/Mark'
import { meta } from '@skin/meta'
import { MINIMUM_PASSWORD_LENGTH } from '../../types/account'

// An auth screen, in the sign-in screen's language: the same card, the same field styling, the same
// refusal block. Presentation only — it never decides whether a password is acceptable, it only says
// the floor up front so nobody is refused for a rule they were not told. The API is the judge.

export interface PasswordFormProps {
  /** they arrived here because an admin reset them, not because they chose to come */
  forced: boolean
  busy: boolean
  /** the API's own words, when it refused */
  refusal: string | null
  onSubmit: (currentPassword: string, newPassword: string) => void
  /** where Cancel goes back to; absent when they are being held here */
  onCancel?: () => void
}

export function PasswordForm({ forced, busy, refusal, onSubmit, onCancel }: PasswordFormProps) {
  const formRef = useRef<HTMLFormElement>(null)
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')

  // The one thing the browser answers for itself: the two boxes it asked the person to type twice.
  // Everything else — length, sameness, whether the current one is right — comes back from the API.
  const mismatch = confirm.length > 0 && next !== confirm

  function submit(e: FormEvent) {
    e.preventDefault()
    if (mismatch) return
    onSubmit(current, next)
  }

  return (
    <div className="radar-stage">
      <div className="login-canvas">
        <form ref={formRef} className="login-card" onSubmit={submit}>
          <div className="brand brand-dark">
            <Mark size={36} />
            <span className="brand-name">{meta.productName}</span>
          </div>
          <p className="brand-sub">{forced ? 'Choose your password' : 'Change your password'}</p>
          <p className="tagline">
            {forced
              ? 'Your admin gave you a temporary password. Choose your own to continue.'
              : `Pick something only you know, at least ${MINIMUM_PASSWORD_LENGTH} characters. A phrase you can remember beats a short password with symbols in it.`}
          </p>

          <label>
            {forced ? 'Temporary password' : 'Current password'}
            <input
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              autoFocus
            />
          </label>
          <label>
            New password
            <input
              type="password"
              autoComplete="new-password"
              minLength={MINIMUM_PASSWORD_LENGTH}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
            />
          </label>
          <label>
            New password again
            <input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </label>

          {mismatch && (
            <div className="refusal" role="alert">
              <p>The two new passwords are not the same.</p>
            </div>
          )}
          {refusal && !mismatch && (
            <div className="refusal" role="alert">
              <p>{refusal}</p>
            </div>
          )}

          <button type="submit" className="button primary" disabled={busy || mismatch}>
            {busy ? meta.busyLabel : 'Set password'}
          </button>
          {onCancel && !forced && (
            <button type="button" className="link-button" onClick={onCancel}>
              Cancel and go back
            </button>
          )}

          <div className="login-status">
            <span>Minimum</span>
            <strong>{MINIMUM_PASSWORD_LENGTH} characters</strong>
            <span>Other sessions</span>
            <strong>signed out</strong>
          </div>
        </form>
      </div>
    </div>
  )
}
